import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import {
  ActiveModeCommands,
  ArrayValue,
  ConsolidatedFeature,
  ConsolidatorDevice,
  Feature,
  FeatureDeprecated,
  FeatureNames,
  Gateway,
  IsStringValueProperty,
  ScheduleCommands,
  ScheduleProperties,
  SRCLegacyProgramsActiveValue,
  SRCProgramsActiveValue,
} from '@vi-iot/client';

import {
  averageValueOfTheArray,
  DevicesSelectors,
  DevicesStore,
  EnergyHistoricalDataSelectors,
  InstallationDetailsSelectors,
  InstallationFeaturesStore,
  isReady,
  sumArray,
  sumArrays,
} from '@store';

import {
  Chart,
  ChartBarModel,
  ChartBarPartModel,
  SeriesSummary,
} from '../../features/chart-bar/models/chart.model';
import { getTransformedEnergyHoursDetails } from '../../operational-data-common/helpers/energy';
import { Unit } from '../../shared/common/units';
import { DatesService } from '../../shared/services/dates.service';
import {
  ExpectedPeriod,
  ExtendedRawConsumptionProperties,
  PeriodName,
  RoundingRule,
} from '../_models';
import { GatewayActions } from '../gateway/gateway.actions';
import { HemsSelectors } from '../hems/hems.selectors';
import { OperatingModesActions } from '../operating-modes/operating-modes.actions';
import { OperatingModesSelectors } from '../operating-modes/operating-modes.selectors';
import dayjs from 'dayjs';

export interface Indicator {
  loading: boolean;
  loaded: boolean;
  error: any;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  constructor(
    private store: Store,
    private datesService: DatesService,
    private translate: TranslateService,
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  /**@deprecated Please use InstallationFeaturesStore.selectInstallationDeviceFeature*/
  selectIsHolidayProgramOrHolidayAtHomeActive(): Observable<boolean> {
    return this.store.select(DevicesSelectors.isHolidayProgramOrHolidayAtHomeActive());
  }
  /**@deprecated Please use InstallationFeaturesStore.selectInstallationDeviceFeature*/
  selectIsSystemPanelVisible(): Observable<boolean> {
    return this.store.select(DevicesSelectors.isSystemPanelVisible());
  }

  selectPowerProductionChartData(deviceId?: string): Observable<Chart> {
    return combineLatest([
      this.store
        .select(DevicesSelectors.getPowerProduction(deviceId))
        .pipe(distinctUntilChanged(isEqual)),
    ]).pipe(
      filter(([rawConsumptionProperties]) => {
        return rawConsumptionProperties;
      }),
      map(([rawPowerProductionProperties]) => {
        const expectedPeriods = this.calculateAvailablePeriods({
          rawConsumptionProperties: rawPowerProductionProperties,
        });

        const powerProductionData = this.convertRawConsumptionPropertiesToChartData(
          rawPowerProductionProperties,
          expectedPeriods,
          []
        );

        // After chart data has been constructed, check value and adjust unit to either Wh, kWh or MWh
        return this.adjustPowerPropertiesUnitsAndValues(powerProductionData);
      })
    );
  }

  selectSolarPowerProductionChartData(deviceId?: string): Observable<Chart> {
    return combineLatest([
      this.store
        .select(DevicesSelectors.getSolarPowerProduction(deviceId))
        .pipe(distinctUntilChanged(isEqual)),
    ]).pipe(
      filter(([rawConsumptionProperties]) => {
        return !!rawConsumptionProperties;
      }),
      map(([rawSolarPowerProductionProperties]) => {
        const expectedPeriods = this.calculateAvailablePeriods({
          rawConsumptionProperties: rawSolarPowerProductionProperties,
        });

        const powerProductionData = this.convertRawConsumptionPropertiesToChartData(
          rawSolarPowerProductionProperties,
          expectedPeriods,
          []
        );

        // After chart data has been constructed, check value and adjust unit to either Wh, kWh or MWh
        return this.adjustPowerPropertiesUnitsAndValues(powerProductionData);
      })
    );
  }

  selectGasConsumptionChartData(deviceId?: string): Observable<Chart | undefined> {
    return combineLatest([
      this.store
        .select(DevicesSelectors.getGasConsumption(deviceId))
        .pipe(distinctUntilChanged(isEqual)),
    ]).pipe(
      filter(([rawConsumptionProperties]) => {
        return !!rawConsumptionProperties;
      }),
      map(([rawConsumptionProperties]) => {
        const roundingRules: RoundingRule[] = [{ unit: 'cubicMeter', roundingRule: '1.1-1' }];
        const expectedPeriods = this.calculateAvailablePeriods({
          rawConsumptionProperties,
        });

        const chartData = this.convertRawConsumptionPropertiesToChartData(
          rawConsumptionProperties,
          expectedPeriods,
          roundingRules
        );

        return this.calculateTotalFromFeatures(chartData, 'FEATURE.HEATING.GAS.CONSUMPTION.TOTAL');
      })
    );
  }

  selectPowerConsumptionChartData(deviceId?: string): Observable<Chart> {
    return combineLatest([
      this.store
        .select(DevicesSelectors.getDetailedPowerConsumption(deviceId))
        .pipe(distinctUntilChanged(isEqual)),
      this.store
        .select(EnergyHistoricalDataSelectors.getLegacyChartData())
        .pipe(distinctUntilChanged(isEqual)),
      this.store.select(HemsSelectors.selectHemsGatewaySerialIfNoError),
      this.devicesStore.getSelectedDevice$,
      this.devicesStore.isEcotronicDevice$,
    ]).pipe(
      filter(
        ([e3powerConsumption, legacyPowerConsumption, hemsGateway, selectedDevice]) =>
          selectedDevice.gatewaySerial !== hemsGateway &&
          ((Boolean(e3powerConsumption) && Boolean(e3powerConsumption.length)) ||
            Boolean(legacyPowerConsumption))
      ),
      map(
        ([
          e3powerConsumption,
          legacyPowerConsumption,
          _hemsGateway,
          _selectedDevice,
          isEcotronic,
        ]) => {
          const isE3 = e3powerConsumption?.length;

          if (isE3) {
            return [e3powerConsumption, true];
          } else {
            return [legacyPowerConsumption, false, isEcotronic];
          }
        }
      ),
      map(([rawConsumptionProperties, isE3, isEcotronic]) => {
        const expectedPeriods = this.calculateAvailablePeriods({
          rawConsumptionProperties,
          getAllAvailablePeriods: true,
        });

        const powerConsumptionData = this.convertRawConsumptionPropertiesToChartData(
          rawConsumptionProperties,
          expectedPeriods,
          []
        );

        let adjustedData: Chart;

        if (isE3) {
          const totalAdjustedChartData = this.calculateTotalFromFeatures(
            powerConsumptionData,
            'FEATURE.HEATING.POWER.CONSUMPTION.TOTAL'
          );
          // After chart data has been constructed, check value and adjust unit to either Wh, kWh or MWh
          adjustedData = this.adjustPowerPropertiesUnitsAndValues(
            cloneDeep(totalAdjustedChartData)
          );
        } else {
          adjustedData = this.adjustPowerPropertiesUnitsAndValues(
            cloneDeep(powerConsumptionData),
            isEcotronic
          );
        }

        return adjustedData;
      })
    );
  }

  selectHeatProductionChartData(deviceId?: string): Observable<Chart> {
    return combineLatest([
      this.store
        .select(DevicesSelectors.getProducedHeat(deviceId))
        .pipe(distinctUntilChanged(isEqual)),
      this.store.select(HemsSelectors.selectHemsGatewaySerialIfNoError),
      this.devicesStore.getSelectedDevice$,
    ]).pipe(
      filter(([heatProduction, hemsGateway, selectedDevice]) => {
        return heatProduction?.length && selectedDevice.gatewaySerial !== hemsGateway;
      }),
      filter(([rawConsumptionProperties]) => {
        return !!rawConsumptionProperties;
      }),
      map(([rawConsumptionProperties]) => {
        const expectedPeriods = this.calculateAvailablePeriods({
          rawConsumptionProperties,
          getAllAvailablePeriods: true,
        });

        const heatProductionData = this.convertRawConsumptionPropertiesToChartData(
          rawConsumptionProperties,
          expectedPeriods,
          []
        );

        const totalAdjustedChartData = this.calculateTotalFromFeatures(
          heatProductionData,
          'FEATURE.HEATING.HEAT.PRODUCTION.TOTAL'
        );

        // After chart data has been constructed, check value and adjust unit to either Wh, kWh or MWh
        const adjustedData = this.adjustPowerPropertiesUnitsAndValues(
          cloneDeep(totalAdjustedChartData)
        );

        return adjustedData;
      })
    );
  }

  adjustPowerPropertiesUnitsAndValues(powerConsumptionData: Chart, isEcotronic?: Unit): Chart {
    powerConsumptionData?.periods.forEach((timePeriod) => {
      const periodHighestValue = timePeriod.maxValueOfThePeriod;

      const { multiplier, unit } = getTransformedEnergyHoursDetails(
        periodHighestValue,
        !isEcotronic ? Unit.KilowattHour : Unit.Kilogram
      );

      timePeriod.maxValueOfThePeriod = timePeriod.maxValueOfThePeriod * multiplier;

      timePeriod.periodSummary.forEach((summary) => {
        summary.sumValueOfThePeriod = summary.sumValueOfThePeriod * multiplier;
        summary.averageValueOfThePeriod = summary.averageValueOfThePeriod * multiplier;
        summary.unit = unit;
      });

      timePeriod.chartBars.forEach((chartBar) => {
        chartBar.chartBarParts.forEach((chartBarPart) => {
          chartBarPart.unit = unit;
          chartBarPart.value = chartBarPart.value * multiplier;
        });
      });
    });

    return powerConsumptionData;
  }

  /**@deprecated Please use InstallationFeaturesStore.selectInstallationDeviceFeature*/
  selectOperatingModes(
    gatewaySerial: string
  ): Observable<{
    circuits: string[];
    mode: { [key: string]: Feature<IsStringValueProperty, ActiveModeCommands> };
    isDhw: boolean;
  }> {
    return this.store.select(OperatingModesSelectors.getOperatingModes(gatewaySerial));
  }

  /** Select indicators from store */

  selectDeviceIndicators(): Observable<Indicator> {
    return this.store.select(DevicesSelectors.getIndicators());
  }

  selectGatewayProperties(): Observable<Gateway> {
    return this.store.select(InstallationDetailsSelectors.getSelectedGateway());
  }

  selectIsDhwOneTimeChargeEnabled(): Observable<boolean> {
    return this.store.select(DevicesSelectors.isDhwOneTimeChargeEnabled());
  }
  /**@deprecated Please use InstallationFeaturesStore.getAllGatewayFeatures()*/
  fetchGatewayFeature(featureName: string): void {
    this.store.dispatch(GatewayActions.FetchFeature({ featureName }));
  }

  liveUpdateOperatingMode<T, K>(
    featureName: string,
    feature: FeatureDeprecated<T, K>,
    gatewaySerial: string
  ): void {
    this.store.dispatch(
      OperatingModesActions.liveUpdateFeature({ featureName, feature, gatewaySerial })
    );
  }

  /** Create schedule modes */

  getScheduleModes(
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): { name: string }[] | undefined {
    const modes = feature?.commands?.setSchedule?.params?.newSchedule?.constraints?.modes;
    if (modes && isReady(feature)) {
      const scheduleModes = feature.commands.setSchedule.params.newSchedule.constraints.modes.map(
        (mode) => ({
          name: mode,
        })
      );
      scheduleModes.push({
        name: feature.commands.setSchedule.params.newSchedule.constraints.defaultMode,
      });

      return scheduleModes;
    }

    return undefined;
  }

  calculateAvailablePeriods(config: {
    rawConsumptionProperties: ExtendedRawConsumptionProperties[];
    minDate?: string;
    getAllAvailablePeriods?: boolean;
  }): ExpectedPeriod[] {
    let amountOfYears = this.getAmountOfTime(config.rawConsumptionProperties[0], 'year') || 2;
    let amountOfWeeks = this.getAmountOfTime(config.rawConsumptionProperties[0], 'week') || 12;

    const allAvailablePeriods = [
      {
        name: 'day' as PeriodName,
        value: this.datesService.getPreviousDayKeys({ minDate: config.minDate }).reverse(),
        designationKey: this.translate.instant('GENERAL.TIME_DATE.DAYS'),
        designationKeySingular: 'GENERAL.TIME_DATE.DAY',
      },
      {
        name: 'week' as PeriodName,
        value: this.datesService
          .getPreviousWeeks({ minDate: config.minDate, amountOfWeeks })
          .reverse(),
        designationKey: this.translate.instant('GENERAL.TIME_DATE.WEEKS'),
        designationKeySingular: 'GENERAL.TIME_DATE.WEEK',
      },
      {
        name: 'month' as PeriodName,
        value: this.datesService.getPreviousMonthKeys({ minDate: config.minDate }).reverse(),
        designationKey: this.translate.instant('GENERAL.TIME_DATE.MONTHS'),
        designationKeySingular: 'GENERAL.TIME_DATE.MONTH',
      },
      {
        name: 'year' as PeriodName,
        value: this.datesService
          .getPreviousYears({ minDate: config.minDate, amountOfYears })
          .reverse(),
        designationKey: this.translate.instant('GENERAL.TIME_DATE.YEARS'),
        designationKeySingular: 'GENERAL.TIME_DATE.YEAR',
      },
    ] as ExpectedPeriod[];

    if (!!config.getAllAvailablePeriods) {
      return allAvailablePeriods;
    }
    const calculatedAvailablePeriods: ExpectedPeriod[] = [];
    allAvailablePeriods.forEach((period: ExpectedPeriod) => {
      let displayPeriod = false;
      config.rawConsumptionProperties.forEach((properties) => {
        if (properties[period.name]?.value?.length > 0) {
          displayPeriod = true;

          return;
        }
      });
      if (displayPeriod) {
        calculatedAvailablePeriods.push(period);
      }
    });

    return calculatedAvailablePeriods;
  }

  convertRawConsumptionPropertiesToChartData(
    rawConsumptionProperties: ExtendedRawConsumptionProperties[],
    expectedPeriods: ExpectedPeriod[],
    roundingRules: RoundingRule[]
  ): Chart | undefined {
    if (rawConsumptionProperties.length > 0 && expectedPeriods.length > 0) {
      const chart: Chart = {
        periods: [],
      };
      const today = dayjs(new Date());

      const rawConsumptionPropertiesByDayValueReadAt = cloneDeep(rawConsumptionProperties).map(
        (item) => {
          if (
            item.dayValueReadAt &&
            item.weekValueReadAt &&
            item.monthValueReadAt &&
            item.yearValueReadAt
          ) {
            const momentDayValueAt = dayjs(item.dayValueReadAt.value);
            const momentWeekValueAt = dayjs(item.weekValueReadAt.value);
            const momentMonthValueAt = dayjs(item.monthValueReadAt.value);
            const momentYearValueAt = dayjs(item.yearValueReadAt.value);

            return {
              ...item,
              day: this.fixValueOfTime(
                this.getNumberOfEmptyTime(today, momentDayValueAt, 'day'),
                item.day
              ),
              week: this.fixValueOfTime(
                this.getNumberOfEmptyTime(today, momentWeekValueAt, 'week'),
                item.week
              ),
              month: this.fixValueOfTime(
                this.getNumberOfEmptyTime(today, momentMonthValueAt, 'month'),
                item.month
              ),
              year: this.fixValueOfTime(
                this.getNumberOfEmptyTime(today, momentYearValueAt, 'year'),
                item.year
              ),
            };
          }

          return item;
        }
      );

      expectedPeriods.forEach((expectedPeriod: ExpectedPeriod) => {
        const chartBars: ChartBarModel[] = [];
        const periodSummary: SeriesSummary[] = [];
        expectedPeriod.value.forEach((_, index) => {
          const chartBarPartModels: ChartBarPartModel[] = [];
          rawConsumptionPropertiesByDayValueReadAt.forEach((properties) => {
            let rounding = roundingRules.find((r) => r.unit === properties.unit.value);

            // display of value is determined by presence of date period
            // not by the presence of values eg. 0
            // see this.calculateAvailablePeriods()
            const realValuePresent = !!expectedPeriod.value[index];

            if (realValuePresent && properties[expectedPeriod.name].value) {
              chartBarPartModels.unshift({
                color: properties.color,
                value: properties[expectedPeriod.name].value[index] || 0,
                designationKey: properties.designationKey,
                unit: properties.unit.value,
                roundingRule: rounding ? rounding.roundingRule : null,
              });
            }
          });
          chartBars.push({
            designationKey: expectedPeriod.value[index],
            chartBarParts: chartBarPartModels,
          });
        });

        rawConsumptionPropertiesByDayValueReadAt.forEach((properties) => {
          let rounding = roundingRules.find((r) => r.unit === properties.unit.value);
          periodSummary.push({
            designationKey: properties.designationKey,
            color: properties.color,
            unit: properties.unit.value,
            period: this.datesService.getPeriod(
              expectedPeriod.name,
              expectedPeriod.value.length - 1
            ),
            sumValueOfThePeriod:
              properties.unit.value !== 'percent'
                ? sumArray(properties[expectedPeriod.name].value?.slice(1))
                : null,
            averageValueOfThePeriod: averageValueOfTheArray(
              properties[expectedPeriod.name].value?.slice(1)
            ),
            roundingRule: rounding ? rounding.roundingRule : null,
          });
        });

        const maxValues: number[][] = [];
        rawConsumptionPropertiesByDayValueReadAt.forEach((period) => {
          if (period.color !== null) {
            maxValues.push(period[expectedPeriod.name].value);
          }
        });
        const totalValues = sumArrays(...maxValues);

        chart.periods.push({
          chartBars,
          maxValueOfThePeriod: Math.max(...totalValues),
          periodDesignationKey: expectedPeriod.designationKey,
          periodDesignationKeySingular: expectedPeriod.designationKeySingular,
          periodSummary,
        });
      });

      return chart;
    }
  }

  getSRCOperatingProgramHeating(
    activeProgram: SRCProgramsActiveValue | SRCLegacyProgramsActiveValue
  ): SRCProgramsActiveValue | SRCLegacyProgramsActiveValue {
    switch (activeProgram) {
      // Backward compatibility - remove after deprecation of 516 and 48 versions of SRC
      case SRCLegacyProgramsActiveValue.normal:
      case SRCLegacyProgramsActiveValue.comfort:
      case SRCLegacyProgramsActiveValue.reduced:
      case SRCLegacyProgramsActiveValue.manual:
      case SRCLegacyProgramsActiveValue.manualTillNextSchedule:
        return activeProgram;
      case SRCProgramsActiveValue.normalEnergySaving:
      case SRCProgramsActiveValue.normalHeating:
      case SRCProgramsActiveValue.normalCooling:
        return SRCProgramsActiveValue.normalHeating;
      case SRCProgramsActiveValue.reducedEnergySaving:
      case SRCProgramsActiveValue.reducedHeating:
      case SRCProgramsActiveValue.reducedCooling:
        return SRCProgramsActiveValue.reducedHeating;
      case SRCProgramsActiveValue.comfortEnergySaving:
      case SRCProgramsActiveValue.comfortHeating:
      case SRCProgramsActiveValue.comfortCooling:
        return SRCProgramsActiveValue.comfortHeating;
      case SRCProgramsActiveValue.manualEnergySaving:
      case SRCProgramsActiveValue.manualHeating:
      case SRCProgramsActiveValue.manualCooling:
        return SRCProgramsActiveValue.manualHeating;
      case SRCProgramsActiveValue.manualTillNextScheduleEnergySaving:
      case SRCProgramsActiveValue.manualTillNextScheduleHeating:
      case SRCProgramsActiveValue.manualTillNextScheduleCooling:
        return SRCProgramsActiveValue.manualTillNextScheduleHeating;
    }
  }

  getSRCOperatingProgramCooling(
    activeProgram: SRCProgramsActiveValue | SRCLegacyProgramsActiveValue
  ): SRCProgramsActiveValue | SRCLegacyProgramsActiveValue {
    switch (activeProgram) {
      case SRCProgramsActiveValue.normalEnergySaving:
      case SRCProgramsActiveValue.normalHeating:
      case SRCProgramsActiveValue.normalCooling:
        return SRCProgramsActiveValue.normalCooling;
      case SRCProgramsActiveValue.reducedEnergySaving:
      case SRCProgramsActiveValue.reducedHeating:
      case SRCProgramsActiveValue.reducedCooling:
        return SRCProgramsActiveValue.reducedCooling;
      case SRCProgramsActiveValue.comfortEnergySaving:
      case SRCProgramsActiveValue.comfortHeating:
      case SRCProgramsActiveValue.comfortCooling:
        return SRCProgramsActiveValue.comfortCooling;
      case SRCProgramsActiveValue.manualEnergySaving:
      case SRCProgramsActiveValue.manualHeating:
      case SRCProgramsActiveValue.manualCooling:
        return SRCProgramsActiveValue.manualCooling;
      case SRCProgramsActiveValue.manualTillNextScheduleEnergySaving:
      case SRCProgramsActiveValue.manualTillNextScheduleHeating:
      case SRCProgramsActiveValue.manualTillNextScheduleCooling:
        return SRCProgramsActiveValue.manualTillNextScheduleCooling;
    }
  }

  private getAmountOfTime(
    properties: ExtendedRawConsumptionProperties,
    periodName: PeriodName
  ): number {
    if (properties && properties[periodName]) {
      return properties[periodName].value.length - 1;
    }

    return 0;
  }

  getConsolidatedFeatures(featureName: string): Observable<Feature<ConsolidatedFeature, unknown>> {
    return this.devicesStore.idsOfV2Cascade$.pipe(
      switchMap((ids) => {
        const featureChecks = ids.map((id) =>
          this.installationFeaturesStore.selectInstallationDeviceFeature<
            ConsolidatedFeature,
            unknown
          >(featureName, id)
        );

        return combineLatest(featureChecks);
      }),
      map((result) => {
        const featureDevice = result.find((feature) => !!feature);

        if (
          featureName === FeatureNames.stateOfCapacityNominalConsolidated &&
          !featureDevice.properties.devices
        ) {
          return;
        }

        return featureDevice;
      })
    );
  }

  private fixValueOfTime(amountOfDate: number, arrayValue: ArrayValue<number>): ArrayValue<number> {
    if (amountOfDate > arrayValue.value.length) {
      arrayValue.value = arrayValue.value.map((value) => {
        value = 0;

        return value;
      });
    } else {
      for (let num = 0; num < amountOfDate; num++) {
        arrayValue.value = arrayValue.value.slice(0, -1);
        arrayValue.value.unshift(0);
      }
    }

    return arrayValue;
  }

  private calculateTotalFromFeatures(powerConsumptionData: Chart, designationKey: string): Chart {
    const chart = cloneDeep(powerConsumptionData);

    chart?.periods[0]?.chartBars.forEach((singleDayData) => {
      singleDayData.chartBarParts = singleDayData.chartBarParts.map((singleBarBoxPart) => {
        if (singleBarBoxPart.designationKey === designationKey) {
          singleBarBoxPart.value = singleDayData.chartBarParts.reduce((sum, singleGraphPart) => {
            return singleGraphPart.designationKey !== designationKey
              ? sum + singleGraphPart.value
              : sum;
          }, 0);
        }

        return singleBarBoxPart;
      });
    });

    return chart;
  }

  private getNumberOfEmptyTime(
    today: dayjs.Dayjs,
    dayValueReadAt: dayjs.Dayjs,
    typeOfTime: PeriodName
  ): number {
    const dayjs = this.datesService.getDayjsWithIsoDate();
    const numberOfEmptyDate = dayjs(today.format('YYYY-MM-DD')).diff(
      dayjs(dayValueReadAt.format('YYYY-MM-DD')),
      'days'
    );
    switch (typeOfTime) {
      case 'day':
        return numberOfEmptyDate;
      case 'week':
        const remainDayInValueReadAtWeek = 7 - dayjs(dayValueReadAt).isoWeekday();

        return numberOfEmptyDate > remainDayInValueReadAtWeek
          ? Math.ceil((numberOfEmptyDate - remainDayInValueReadAtWeek) / 7)
          : 0;
      case 'month':
        const remainDayInValueReadAtMonth = dayValueReadAt.daysInMonth() - dayValueReadAt.date();

        return numberOfEmptyDate > remainDayInValueReadAtMonth
          ? Math.ceil((numberOfEmptyDate - remainDayInValueReadAtMonth) / 30)
          : 0;
      case 'year':
        return today.year() - dayValueReadAt.year();
    }
  }

  getCapacityBatteryByDevicesId(
    featureName: string,
    deviceId: string
  ): Observable<Feature<ConsolidatedFeature, unknown> | ConsolidatorDevice> {
    return this.devicesStore.idsOfVitoCharge$.pipe(
      switchMap((ids) => {
        const featureChecks = ids.map((id) =>
          this.installationFeaturesStore.selectInstallationDeviceFeature<
            ConsolidatedFeature,
            unknown
          >(featureName, id)
        );

        return combineLatest(featureChecks);
      }),
      map((result) => {
        const featureFound = result.find(
          (feature) =>
            !!feature && (feature.deviceId === deviceId || feature?.properties?.devices?.value)
        );

        if (featureFound?.properties?.devices?.value) {
          return featureFound.properties.devices.value.find((value) => value.id === deviceId);
        } else {
          return featureFound;
        }
      })
    );
  }

  isFeatureConsolidate(
    value: ConsolidatorDevice | Feature<ConsolidatedFeature, unknown>
  ): value is Feature<ConsolidatedFeature, unknown> {
    return !!(
      value &&
      typeof value === 'object' &&
      'gatewayId' in value &&
      'feature' in value &&
      'timestamp' in value
    );
  }
}
